import * as React from "react";
import styled, { css } from "styled-components";
import dateFormat from "date-fns/format";

import i18n from "../../lib/i18n";
import { media } from "../../common/utils/style";
import { NavigatableSection, SectionInner, SectionTitle } from "./Section";
import Translate from "../../common/components/Translate";
import ImageComponent, { Image } from "../../common/components/Image";
import Icon from "../../common/components/Icon";

type Company = WtdModels.Partial<
  WtdModels.Company,
  | "longitude"
  | "latitude"
  | "address_suffix"
  | "address_prefix"
  | "name"
  | "twitter"
  | "facebook"
  | "font_color_code"
  | "url"
  | "founded_on"
  | "founder"
  | "payroll_number"
  | "misc_tag_names",
  {
    avatar: WtdModels.HasOne<Image>;
  }
>;

export interface BasicInfoSectionModels {
  Company: Company;
}

// TODO(stomk): Load from .env via process.env
const GMAP_API_KEY = "AIzaSyDo7TeF2_KjUFssCiRmvtN-lIq2gJtQtUg";

const buildGmapUrl = (company: Company) => {
  const centerQuery = company.latitude && company.longitude ? `&center=${company.latitude},${company.longitude}` : "";
  const url = `https://www.google.com/maps/embed/v1/place?key=${GMAP_API_KEY}&q=${encodeURIComponent(
    company.address_suffix ? `${company.address_prefix} ${company.address_suffix}` : `${company.address_prefix}`
  )}${centerQuery}&zoom=15`;
  return url;
};

interface Props {
  company: Company;
  locale: string;
  editable?: boolean;
  onEdit?(): void;
}

class BasicInfoSection extends React.PureComponent<Props> {
  public onKeyDownEdit = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      this.props.onEdit!();
    }
  };

  public render() {
    const { company, locale, editable, onEdit } = this.props;
    return (
      <NavigatableSection id="basic_info" displayName={i18n.translate("company.home.sections.basic_info", { locale })}>
        <SectionInner>
          <Wrapper>
            <LeftCol>
              <SectionTitle>
                <Translate id="company.home.sections.basic_info" />
                {editable && <EditIcon name="wt-icon-pencil" onClick={onEdit} onKeyDown={this.onKeyDownEdit} />}
              </SectionTitle>
              <CompanyTitle>
                {company.avatar && <Avatar image={company.avatar} size="50x50m" alt={company.name} />}
                <div>
                  {company.name && <CompanyName>{company.name}</CompanyName>}
                  {(company.twitter || company.facebook) && (
                    <SNSLinks>
                      {company.facebook && (
                        <SNSLink href={company.facebook} target="_blank">
                          <SNSIcon className="icon-facebook" color={company.font_color_code} />
                        </SNSLink>
                      )}
                      {company.twitter && (
                        <SNSLink href={company.twitter} target="_blank">
                          <SNSIcon className="icon-twitter" color={company.font_color_code} />
                        </SNSLink>
                      )}
                    </SNSLinks>
                  )}
                </div>
              </CompanyTitle>
              {(company.address_prefix || company.address_suffix) && (
                <ListItem>
                  <WrappedIcon name="wt-icon-location" />
                  <CompanyInfoDescription>
                    {company.address_prefix} {company.address_suffix}
                  </CompanyInfoDescription>
                </ListItem>
              )}
              {company.url && (
                <ListItem>
                  <WrappedIcon name="wt-icon-link" />
                  <CompanyInfoDescription>
                    <a href={company.url} target="_blank">
                      {company.url}
                    </a>
                  </CompanyInfoDescription>
                </ListItem>
              )}
              {company.founded_on && (
                <ListItem>
                  <WrappedFaIcon className="icon-flag" />
                  <CompanyInfoDescription>
                    <Translate id="company.founded_on" values={{ date: dateFormat(company.founded_on, "YYYY/M") }} />
                  </CompanyInfoDescription>
                </ListItem>
              )}
              {company.founder && (
                <ListItem>
                  <WrappedIcon name="wt-icon-person" />
                  <CompanyInfoDescription>
                    <Translate id="company.founded_by" values={{ user: company.founder }} />
                  </CompanyInfoDescription>
                </ListItem>
              )}
              {company.payroll_number && (
                <ListItem>
                  <WrappedFaIcon className="icon-group" />
                  <CompanyInfoDescription>
                    <Translate id="company.count_of_members" values={{ count: company.payroll_number }} />
                  </CompanyInfoDescription>
                </ListItem>
              )}
              {company.misc_tag_names.length > 0 && (
                <ListItem>
                  <WrappedIcon name="wt-icon-star" />
                  <CompanyInfoDescription>{company.misc_tag_names.join(" / ")}</CompanyInfoDescription>
                </ListItem>
              )}
            </LeftCol>
            {company.address_prefix && (
              <RightCol>
                <MapWrapper>
                  <Map src={buildGmapUrl(company)} />
                </MapWrapper>
              </RightCol>
            )}
          </Wrapper>
        </SectionInner>
      </NavigatableSection>
    );
  }
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  @media ${media.large} {
    flex-direction: row;
  }
`;

const LeftCol = styled.div`
  margin-bottom: 40px;
  @media ${media.large} {
    width: 400px;
    flex-grow: 0;
    margin-right: 80px;
  }
`;

const RightCol = styled.div`
  flex-grow: 1;
  justify-self: flex-end;
`;

const CompanyTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 20px;
  @media ${media.large} {
    margin-bottom: 40px;
  }
`;

const Avatar = styled(ImageComponent)`
  top: -30px;
  left: 17px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: solid 1.5px #f0f0f0;
  box-sizing: border-box;
  margin-right: 18px;
  @media ${media.large} {
    position: static;
    width: 50px;
    height: 50px;
    margin-right: 12px;
    border: none;
  }
`;

const CompanyName = styled.div`
  font-weight: 700;
  font-size: 20px;
  letter-spacing: 0.3px;
  color: #000;
  @media ${media.large} {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.2px;
    line-height: 1.3;
  }
`;

const SNSLinks = styled.div`
  display: flex;
  align-items: center;
  margin-top: 6px;
`;

const SNSLink = styled.a`
  width: 18px;
  text-align: center;
  &:hover {
    text-decoration: none;
  }
  & + & {
    margin-left: 7px;
  }
`;

const SNSIcon = styled.i`
  font-size: 18px;
  color: ${(props: { color: string }) => props.color} !important;
`;

const ListItem = styled.li`
  font-size: 13px;
  margin-bottom: 13px;
  display: flex;
  &:last-child {
    margin-bottom: 0;
  }
`;

const IconBase = css`
  color: rgba(0, 0, 0, 0.5);
`;

const WrappedIcon = styled(Icon)`
  ${IconBase};
`;

const WrappedFaIcon = styled.i`
  ${IconBase};
  margin-top: 5px;
`;

const CompanyInfoDescription = styled.div`
  margin-left: 12px;
  text-align: left;
  color: rgba(0, 0, 0, 0.7);
`;

const MapWrapper = styled.div`
  width: 100vw;
  height: 350px;
  margin-left: -16px;
  @media ${media.large} {
    width: 100%;
    height: 470px;
    margin-left: 0;
  }
`;

const Map = styled.iframe`
  display: block;
  border: none;
  width: 100%;
  height: 100%;
  @media ${media.large} {
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
  }
`;

const EditIcon = styled(Icon).attrs({
  role: "button",
  tabIndex: 0,
})`
  margin-left: auto;
  font-size: 16px;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.6;
  }
`;

export default BasicInfoSection;



// WEBPACK FOOTER //
// ./frontend/src/companies/components/BasicInfoSection.tsx